import { Injectable } from '@angular/core';
import { routes } from '../app.routes';
import { ROUTE_TITLES } from '../app';
import {
  ACCESS_ROLES,
  AccessCell,
  AccessMatrixRow,
  AccessRole,
  ROLE_IMPLIES
} from '../models/access-matrix.model';

/**
 * Builds the role/page access matrix by reading the application's own route table.
 *
 * The matrix is computed, never hand-written: a route whose `data.roles` changes shows the
 * new access the next time this page is opened. Routes with no AuthGuard (login, the public
 * token-based pages, the wildcard) are excluded — they are not role-governed.
 */
@Injectable({ providedIn: 'root' })
export class AccessMatrixService {
  /**
   * Functional areas, matched against the route path by prefix. First match wins, so more
   * specific prefixes are listed before the generic ones.
   */
  // Area names deliberately match the sidebar's own group labels ("Work Log", not
  // "Timesheets"; "Report", not "Reports"), so a row here is findable in the menu.
  private static readonly AREA_RULES: { prefix: string; area: string }[] = [
    { prefix: 'dashboard/', area: 'Dashboard' },
    { prefix: 'presales/', area: 'Presales' },
    { prefix: 'onboarding', area: 'Onboarding' },
    { prefix: 'offboarding', area: 'Offboarding' },
    { prefix: 'outings', area: 'Team Outings' },
    { prefix: 'timesheet', area: 'Work Log' },
    { prefix: 'contracts-', area: 'Finance' },
    { prefix: 'invoic', area: 'Finance' },
    { prefix: 'allocations', area: 'Allocation Plans' },
    { prefix: 'forecast', area: 'Allocation Plans' },
    { prefix: 'future-allocation', area: 'Allocation Plans' },
    { prefix: 'availability-report', area: 'Report' },
    { prefix: 'effort-variance-report', area: 'Report' },
    { prefix: 'project-utilization-report', area: 'Report' },
    { prefix: 'employee-projects-history', area: 'Report' },
    { prefix: 'audit-logs', area: 'Administration' },
    { prefix: 'notification-settings', area: 'Administration' },
    { prefix: 'access-matrix', area: 'Administration' },
    { prefix: 'projects', area: 'Projects Management' }
  ];

  /** Settings/master-data paths that would otherwise fall through to "Other". */
  private static readonly MASTER_DATA = new Set([
    'employees', 'cost-centers', 'roles', 'designations', 'categories',
    'service-groups', 'clients', 'companies', 'counties', 'tasks',
    'checklist-management'
  ]);

  /** Every guarded route as a matrix row, sorted by area then page title. */
  buildMatrix(): AccessMatrixRow[] {
    const rows: AccessMatrixRow[] = [];

    for (const route of routes) {
      const declared = route.data?.['roles'] as AccessRole[] | undefined;
      // Only role-governed routes belong in an access matrix. A guarded route with no
      // `roles` is open to any authenticated user and is not a row in a role grid.
      if (!declared || declared.length === 0) continue;

      const path = route.path ?? '';
      const cells = ACCESS_ROLES.map(({ role }) => this.resolveCell(role, declared));

      rows.push({
        path: path === '' ? '/' : `/${path}`,
        title: this.titleFor(path),
        area: this.areaFor(path),
        declaredRoles: declared,
        cells,
        accessCount: cells.filter(c => c.kind !== 'none').length
      });
    }

    return rows.sort(
      (a, b) => a.area.localeCompare(b.area) || a.title.localeCompare(b.title)
    );
  }

  /**
   * Does `role` reach a page that declares `declared`? Mirrors the AuthGuard's `some()`:
   * a direct listing grants access, and so does holding a role that implies a listed one.
   */
  private resolveCell(role: AccessRole, declared: AccessRole[]): AccessCell {
    if (declared.includes(role)) return { role, kind: 'direct' };

    const implied = ROLE_IMPLIES[role];
    if (implied.some(r => declared.includes(r))) return { role, kind: 'inherited' };

    return { role, kind: 'none' };
  }

  private titleFor(path: string): string {
    if (path === '') return 'Default landing page';
    return ROUTE_TITLES[`/${path}`] ?? this.humanize(path);
  }

  /** Fallback title for a path the header map does not name. */
  private humanize(path: string): string {
    const last = path.split('/').pop() ?? path;
    return last
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }

  private areaFor(path: string): string {
    if (path === '') return 'General';
    const rule = AccessMatrixService.AREA_RULES.find(r => path.startsWith(r.prefix));
    if (rule) return rule.area;
    if (AccessMatrixService.MASTER_DATA.has(path)) return 'Settings';
    return 'Other';
  }

}
